import {rosterStaffPriority} from "@/utils/functions";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import PlayerAvatar from "@/components/PlayerAvatar";
import {
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsPreviewNotice,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import CheckIcon from "@mui/icons-material/Check";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

import {useClubSnapshot} from "@/utils/hooks";
import {playerName, staffRoleName} from "@/utils/clubSnapshot";
import {DATA_OWNERS, hasUserAccount, ownersFor} from "@/utils/clubControl";

// Funzioni dello staff di squadra, nell'ordine in cui il DS le legge
const STAFF_GROUPS = [
	{id: "guida", label: "Guida tecnica", match: /^allenatore|vice/i},
	{id: "collab", label: "Collaboratori", match: /collaborator|analist|video/i},
	{
		id: "prep",
		label: "Preparazione",
		match: /preparatore|recupero|performance/i,
	},
	{
		id: "medica",
		label: "Area medica",
		match: /medic|fisioterap|massagg|osteopat/i,
	},
	{
		id: "org",
		label: "Organizzazione",
		match:
			/team manager|accompagnatore|dirigente|direttore|segret|scout|responsabile/i,
	},
];

// Lettura DS dei ruoli osservati, senza requisiti obbligatori di organico
const KEY_ROLES = [
	{
		label: "Allenatore",
		test: (member) => rosterStaffPriority({role: staffRoleName(member)}) === 0,
	},
	{label: "Prep. atletico", match: /preparatore atletico/i},
	{label: "Prep. portieri", match: /portieri/i},
	{label: "Area medica", match: /medic|fisioterap/i},
	{label: "Team manager", match: /team manager|accompagnatore/i},
];

const groupOf = (member) =>
	rosterStaffPriority({role: staffRoleName(member)}) < 2
		? "guida"
		: STAFF_GROUPS.find((g) => g.match.test(staffRoleName(member)))?.id ||
			"altro";

// Allenatore prima del vice, poi per ruolo e cognome
const roleRank = (member) => rosterStaffPriority({role: staffRoleName(member)});

function TeamOrgCard({team, s, onOpenTeam, onAssign, onOpenMember}) {
	const covered = KEY_ROLES.map((r) => ({
		...r,
		ok: team.staff.some((m) =>
			r.test ? r.test(m) : r.match.test(staffRoleName(m)),
		),
	}));
	const coveredCount = covered.filter((r) => r.ok).length;
	const groups = [...STAFF_GROUPS, {id: "altro", label: "Altro"}]
		.map((g) => ({
			...g,
			members: team.staff
				.filter((m) => groupOf(m) === g.id)
				.sort(
					(a, b) =>
						roleRank(a) - roleRank(b) ||
						staffRoleName(a).localeCompare(staffRoleName(b)) ||
						(a.lastName || "").localeCompare(b.lastName || ""),
				),
		}))
		.filter((g) => g.members.length);
	const tone = !team.staff.length
		? "alert"
		: coveredCount === KEY_ROLES.length
			? "ok"
			: "warn";

	return (
		<div className={"ds-org ds-org--" + tone}>
			<div className="ds-org__head">
				<div className="ds-org__title" onClick={onOpenTeam}>
					<span className="ds-org__name">{team.name}</span>
					<span className="ds-org__count">
						{team.staff.length
							? `${team.staff.length} ${team.staff.length === 1 ? "persona" : "persone"}`
							: "Nessuno staff"}
					</span>
				</div>
				<div className="ds-org__coverage">
					<span className="ds-org__coverage-value">
						{coveredCount}/{KEY_ROLES.length}
					</span>
					<span className="ds-org__coverage-label">
						funzioni osservate · lettura DS
					</span>
				</div>
			</div>

			<div className="ds-org__roles">
				{covered.map((r) => (
					<span
						key={r.label}
						className={"ds-org__role" + (r.ok ? " is-ok" : " is-missing")}
						title={r.ok ? "Ruolo coperto" : "Funzione non osservata nei dati"}
					>
						{r.ok && <CheckIcon fontSize="inherit" />}
						{r.label}
					</span>
				))}
			</div>

			{groups.length ? (
				<div className="ds-org__groups">
					{groups.map((g) => (
						<div key={g.id} className="ds-org__group">
							<div className="ds-org__group-label">{g.label}</div>
							{g.members.map((m) => {
								const account = hasUserAccount(s, m);
								return (
									<div
										key={m.id}
										className="ds-org__member"
										onClick={() => onOpenMember(m)}
									>
										<PlayerAvatar
											firstName={m.firstName}
											lastName={m.lastName}
											size="small"
										/>
										<span className="ds-org__member-body">
											<span className="ds-org__member-name">
												{playerName(m)}
											</span>
											<span className="ds-org__member-role">
												{staffRoleName(m)}
											</span>
										</span>
										<span
											className={"ds-org__account" + (account ? " is-on" : "")}
											title={
												account
													? "Corrispondenza per nome con un’utenza; permessi da verificare"
													: "Nessuna corrispondenza nell’elenco utenze consultato"
											}
										>
											{account ? "Utenza" : "No utenza"}
										</span>
									</div>
								);
							})}
						</div>
					))}
				</div>
			) : (
				<div className="ds-org__empty">
					<span>Nessuno staff assegnato a questa squadra.</span>
					<button className="ds-btn" onClick={onAssign}>
						<PersonAddAltIcon fontSize="small" /> Assegna staff
					</button>
				</div>
			)}
		</div>
	);
}

// Esempio per il modulo licenze/contratti staff (non ancora gestito)
const PREVIEW_LICENSES = [
	{
		id: 1,
		name: "Allenatore Prima Squadra",
		license: "UEFA A",
		expiry: "Giu 2028",
		status: "ok",
	},
	{
		id: 2,
		name: "Allenatore Under 17",
		license: "UEFA B",
		expiry: "Giu 2027",
		status: "warn",
	},
	{
		id: 3,
		name: "Preparatore atletico",
		license: "Laurea Scienze Motorie",
		expiry: "—",
		status: "ok",
	},
	{
		id: 4,
		name: "Allenatore Under 15",
		license: "UEFA C (in corso)",
		expiry: "—",
		status: "alert",
	},
];

function DsStaffPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const teams = s?.teams || [];
	const staff = s?.staff || [];
	const byRole = Object.entries(
		staff.reduce((acc, m) => {
			const role = staffRoleName(m);
			acc[role] = (acc[role] || 0) + 1;
			return acc;
		}, {}),
	)
		.map(([role, count]) => ({id: role, role, count}))
		.sort((a, b) => b.count - a.count);
	const assignedIds = new Set(teams.flatMap((t) => t.staff.map((m) => m.id)));
	const unassigned = staff.filter((m) => !assignedIds.has(m.id));

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Staff"
					subtitle="Organigramma tecnico per squadra"
					snapshot={s}
					scope={scope}
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi label="Membri staff" value={staff.length} />
							<DsKpi
								label="Squadre coperte"
								value={`${teams.filter((t) => t.staff.length).length}/${teams.length}`}
							/>
							<DsKpi
								label="Senza squadra"
								value={unassigned.length}
								sub="membri non assegnati"
							/>
							<DsKpi
								label="Utenze attive"
								value={(s.full || s).users.length}
								sub="accessi alla piattaforma"
								tone={(s.full || s).users.length <= 1 ? "yellow" : undefined}
							/>
						</DsKpiRow>

						<DsSection
							title="Organigramma per squadra"
							hint="Ruoli indicati nella rosa, altrimenti quello dell'anagrafica. Copertura DS sperimentale, non un requisito di organico. Clicca una persona per la scheda."
							action={<DsLink to="/manager/staffs">Anagrafica staff</DsLink>}
						>
							<div className="ds-org-grid">
								{teams.map((t) => (
									<TeamOrgCard
										key={t.id}
										team={t}
										s={s}
										onOpenTeam={() => navigate("/ds/squadra/" + t.id)}
										onAssign={() =>
											navigate("/manager/teams/" + t.id + "/staffs")
										}
										onOpenMember={(m) => navigate("/manager/staffs/" + m.id)}
									/>
								))}
							</div>
						</DsSection>

						<DsSection
							title="Referenti e utenze per area"
							hint="Abbinamento sperimentale per ruolo e nome: non certifica autorizzazioni o dati inseriti."
						>
							<DsTable
								columns={[
									{
										key: "label",
										label: "Area",
										render: (r) => <b>{r.label}</b>,
									},
									{
										key: "people",
										label: "Referenti",
										render: (r) =>
											r.people.length ? (
												r.people
													.map(
														(p) =>
															`${p.firstName} ${p.lastName}${p.hasAccount ? " ✓" : ""}`,
													)
													.join(" · ")
											) : (
												<span className="ds-muted">Nessuno</span>
											),
									},
									{
										key: "accounts",
										label: "Con utenza",
										align: "center",
										render: (r) =>
											`${r.people.filter((p) => p.hasAccount).length}/${r.people.length}`,
									},
									{
										key: "status",
										label: "Stato",
										render: (r) =>
											!r.people.length ? (
												<DsStatus
													status="alert"
													label="Nessun ruolo corrispondente"
												/>
											) : r.people.some((p) => p.hasAccount) ? (
												<DsStatus
													status="ok"
													label="Utenza associabile per nome"
												/>
											) : (
												<DsStatus
													status="warn"
													label="Nessuna utenza associabile"
												/>
											),
									},
								]}
								rows={Object.keys(DATA_OWNERS)
									.filter((key) => key !== "gare")
									.map((key) => ({id: key, ...ownersFor(s, key)}))}
							/>
						</DsSection>

						<div className="ds-two-cols">
							<DsSection
								title={
									s.teamScope
										? "Staff per ruolo nella rosa"
										: "Staff per ruolo anagrafico"
								}
							>
								<DsTable
									columns={[
										{key: "role", label: "Ruolo"},
										{key: "count", label: "Persone", align: "center"},
									]}
									rows={byRole}
									empty="Nessun membro staff per la stagione."
								/>
							</DsSection>

							<DsSection title="Licenze e scadenze" preview>
								<DsPreviewNotice>
									Abilitazioni, patentini e contratti dello staff non sono
									ancora gestiti: ecco come appariranno.
								</DsPreviewNotice>
								<div className="mt-3">
									<DsTable
										columns={[
											{key: "name", label: "Ruolo"},
											{key: "license", label: "Abilitazione"},
											{
												key: "expiry",
												label: "Scadenza contratto",
												render: (r) => (
													<DsStatus status={r.status} label={r.expiry} />
												),
											},
										]}
										rows={PREVIEW_LICENSES}
									/>
								</div>
							</DsSection>
						</div>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsStaffPage;
